import { setupCache, CacheRequestConfig } from 'axios-cache-interceptor'
import axios, {
	InternalAxiosRequestConfig,
	AxiosRequestConfig,
	AxiosResponse,
	AxiosError,
	CanceledError,
	HttpStatusCode,
} from 'axios'
import uuid from 'react-uuid'
import { get, delay } from 'lodash'
import keycloak from '../keycloak'
const https = require('https-browserify')

export type APIErrorResponse = {
	id: string
	message: string
}

export type APIConfig<T = AxiosRequestConfig> = T &
	CacheRequestConfig & {
		retry?: number
		retryDelay?: number
		urlParams?: Record<string, any>
	}

const baseUrl = process.env.REACT_APP_API_BASE as string
const smsUrl = process.env.REACT_APP_SMS_API_BASE as string

const userId = (): string => {
	try {
		return JSON.parse(localStorage.user)?.uuid
	} catch (e) {
		console.log('No userProfile stored in localStorage')
		return ''
	}
}

const url = (
	url: string | undefined,
	urlParams: Record<string, string> | undefined,
): string | undefined => {
	if (!url || !urlParams) return url
	return Object.keys(urlParams).reduce(
		(res, key) => (urlParams[key] ? res?.replace(`:${key}`, urlParams[key]) : res),
		url,
	)
}

const API = axios.create({
	httpsAgent: new https.Agent({
		rejectUnauthorized: false,
	}),
	timeout: 13000,
	baseURL: baseUrl,
	headers: {
		'Content-Type': 'application/json;charset=UTF-8',
	},
})

const SMSAPI = axios.create({
	httpsAgent: new https.Agent({
		rejectUnauthorized: false,
	}),
	timeout: 13000,
	baseURL: smsUrl,
	headers: {
		'Content-Type': 'application/json;charset=UTF-8',
	},
})

API.interceptors.request.use(
	(config: APIConfig<InternalAxiosRequestConfig<any>>) => {
		config.baseURL = baseUrl
		config.url = url(config.url, config.urlParams)

		config.timeout = 1000 * 60 * 5
		config.cache = config.cache === undefined ? false : config.cache
		config.httpsAgent = new https.Agent({ rejectUnauthorized: false })

		config.headers.set('Authorization', `Bearer ${keycloak?.token}`)
		config.headers.set('X-Caller-Id', userId())
		config.headers.set('X-Trace-Id', uuid())

		return config
	},
	(error: any) => {
		return Promise.reject(error)
	},
)

API.interceptors.response.use(
	// ({ data }: AxiosResponse) => data,
	(response: AxiosResponse) => {
		return response
	},
	async (error: AxiosError<APIErrorResponse>) => {
		if (error instanceof CanceledError) return Promise.reject(error)

		const { config = {}, response } = error
		const {
			retry = 0,
			retryDelay = 0,
			...rest
		} = config as APIConfig<InternalAxiosRequestConfig<any>>

		if (response) {
			const { data, status: statusCode = HttpStatusCode.InternalServerError } = response

			switch (statusCode) {
				case HttpStatusCode.BadRequest:
					if (retry) {
						await new Promise((resolve) => delay(resolve, retryDelay))
						return await API({ retry: retry - 1, ...rest } as APIConfig<
							InternalAxiosRequestConfig<any>
						>)
					}
					return Promise.reject(data)
				case HttpStatusCode.Unauthorized:
					delay(() => {
						keycloak?.logout?.({ redirectUri: window.location.origin })
						sessionStorage.clear()
					}, 1300)
					return Promise.reject(data)
				case HttpStatusCode.Forbidden:
				default:
					return Promise.reject(data)
			}
		}

		return Promise.reject(error)
	},
)

export default API;
export {SMSAPI};
